import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Breadcrumbs from "../based/Breadcrumbs";
import { CONSTANTS, INITPAGING, NOTIFY, PLATFORM } from "../based/Constants";
import TFUFooter from "../based/content/TableFooter";
import { Notify } from "../based/Notify";
import FinanceServices from "../based/services/FinanceServices";
import OrderServices from "../based/services/OrderServices";

const BANK_TABS = [
    // { value: "ACB", label: "ACB" },
    { value: "MBBANK", label: "MB" },
];

const STATUS_TABS = [
    { value: "all", label: "Tất cả" },
    // { value: "unclassified", label: "Chưa phân loại bút toán" },
    // { value: "classified", label: "Đã phân loại bút toán" },
];

const TRANSACTION_TYPES = [
    { value: "all", label: "Tất cả" },
    { value: "debit", label: "Ghi nợ" },
    { value: "credit", label: "Ghi có" },
];

const AMOUNT_REGEX = /([+-]?\s?\d{1,3}(?:[.,]\d{3})*|\d+)\s?(VND|đ)/gi;
const BALANCE_REGEX = /(?:SD|So du|Số dư)[^\d]*([+-]?\d{1,3}(?:[.,]\d{3})*|\d+)/i;
const CONTENT_REGEX = /(?:^|\s)ND:\s*([^\r\n]*)/i;

export default function SmsManagement() {
    const [bank, setBank] = useState("MBBANK");
    const [status, setStatus] = useState("all");
    const [keyword, setKeyword] = useState("");
    const [type, setType] = useState("all");
    const [messages, setMessages] = useState([]);
    const [paging, setPaging] = useState({ ...INITPAGING, pageSize: 50, orderDirection: "desc", from: "MB" });
    const [isLoading, setLoading] = useState(false);
    const [detailId, setDetailId] = useState(null);

    useEffect(() => {
        getData({ ...paging, pageNumber: 1, from: bank });
    }, [bank]);

    async function getData(nextPaging) {
        setLoading(true);
        const [err, data] = await FinanceServices.GetSmsContentByPage(nextPaging);
        if (!err && data) {
            const listObjects = data.listObjects || [];
            setMessages(listObjects.map(mapSmsContent));
            setPaging(data.paging || nextPaging);
        } else {
            setMessages([]);
            setPaging(nextPaging);
        }
        setLoading(false);
    }

    function handleSearch() {
        getData({ ...paging, pageNumber: 1, from: bank, keyword });
    }

    const tabCounts = useMemo(() => {
        return messages.reduce(
            (result, item) => {
                result.all += 1;
                result[item.classified ? "classified" : "unclassified"] += 1;
                return result;
            },
            { all: 0, classified: 0, unclassified: 0 }
        );
    }, [messages]);

    const filteredMessages = useMemo(() => {
        const searchText = keyword.trim().toLowerCase();
        return messages.filter((item) => {
            const matchStatus = status === "all" || (status === "classified" ? item.classified : !item.classified);
            const matchType = type === "all" || item.transactionType === type;
            const matchKeyword = !searchText || item.content.toLowerCase().includes(searchText) || item.note.toLowerCase().includes(searchText);
            return matchStatus && matchType && matchKeyword;
        });
    }, [messages, status, keyword, type]);

    return (
        <Wrapper className="container-fluid">
            <Breadcrumbs currentPage="Quản lý SMS ngân hàng" />
            <div className="sms-page">
                <div className="bank-tabs">
                    {BANK_TABS.map((item) => (
                        <button key={item.value} className={bank === item.value ? "tab active" : "tab"} onClick={() => setBank(item.value)}>
                            {item.label}
                        </button>
                    ))}
                </div>
                <div className="status-tabs">
                    {STATUS_TABS.map((item) => (
                        <button key={item.value} className={status === item.value ? "tab active" : "tab"} onClick={() => setStatus(item.value)}>
                            {item.label}
                            {tabCounts[item.value] > 0 && <span className="count-badge">{tabCounts[item.value]}</span>}
                        </button>
                    ))}
                </div>
                <div className="filter-bar">
                    <div className="search-box">
                        <input value={keyword} onChange={(e) => setKeyword(e.target.value)} onKeyPress={(e) => e.charCode === 13 && handleSearch()} placeholder="Tìm kiếm SMS" />
                        <button type="button" aria-label="Tìm kiếm" onClick={handleSearch}>
                            <i className="fa fa-search" aria-hidden="true" />
                        </button>
                    </div>
                    <select value={type} onChange={(e) => setType(e.target.value)}>
                        {TRANSACTION_TYPES.map((item) => (
                            <option key={item.value} value={item.value}>
                                {item.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="sms-grid">
                    {isLoading && <div className="empty-state">Đang tải SMS...</div>}
                    {!isLoading && filteredMessages.map((item) => <SmsCard key={item.id} item={item} onDetail={() => setDetailId(item.id)} />)}
                    {!isLoading && filteredMessages.length === 0 && <div className="empty-state">Không có SMS phù hợp</div>}
                </div>
                <div className="sms-footer">
                    <TFUFooter itemToDisplay={2} paging={paging} onFilter={(nextPaging) => getData({ ...nextPaging, from: bank, keyword })} />
                </div>
            </div>
            <SmsDetailModal smsId={detailId} onClose={() => setDetailId(null)} onMapped={() => getData({ ...paging, from: bank, keyword })} />
        </Wrapper>
    );
}

function mapSmsContent(item) {
    const content = getField(item, "content") || "";
    const parsed = parseBankingSms(content);

    return {
        id: getField(item, "id"),
        bank: parsed.bank || normalizeBank(getField(item, "from")) || "",
        time: formatSmsTime(getField(item, "createdDate"), getField(item, "receivedAt")),
        content,
        transactionType: parsed.isCredit === true ? "credit" : parsed.isCredit === false ? "debit" : "",
        amount: parsed.amount != null ? `${parsed.isCredit === false ? "- " : "+ "}${formatMoney(parsed.amount)}` : "",
        balance: parsed.balance != null ? formatMoney(parsed.balance) : "",
        note: parsed.content || "",
        classified: false,
        tags: [],
        orderId: item.orderId,
    };
}

function parseBankingSms(sms) {
    const result = {
        raw: sms,
        bank: detectBank(sms),
        amount: null,
        balance: null,
        isCredit: null,
        content: "",
    };

    if (!sms) return result;

    const matches = Array.from(sms.matchAll(AMOUNT_REGEX));
    if (matches.length > 0) {
        const amountMatch = matches.find((match) => match[1].includes("+") || match[1].includes("-"));
        if (amountMatch) {
            result.amount = parseMoney(amountMatch[1]);
            result.isCredit = amountMatch[1].includes("+");
            result.balance = extractBalance(sms, matches);
        }
    }

    const contentMatch = sms.match(CONTENT_REGEX);
    if (contentMatch) {
        result.content = contentMatch[1].trim();
    }

    return result;
}

function extractBalance(sms, amountMatches) {
    const balanceMatch = sms.match(BALANCE_REGEX);
    if (balanceMatch) {
        return parseMoney(balanceMatch[1]);
    }

    const amounts = amountMatches.map((match) => parseMoney(match[1])).filter((value) => value != null);
    if (!amounts.length) return null;
    return Math.max(...amounts);
}

function parseMoney(raw) {
    if (!raw) return null;
    const cleaned = raw.replace(/\s/g, "").replace(/,/g, "").replace(/\./g, "").replace(/[+-]/g, "");
    const value = Number(cleaned);
    return Number.isFinite(value) ? value : null;
}

function formatMoney(amount) {
    if (amount == null) return "";
    return `${new Intl.NumberFormat("vi-VN").format(amount)}đ`;
}

function formatSmsTime(createdDate, receivedAt) {
    const date = createdDate ? new Date(createdDate) : receivedAt ? new Date(Number(receivedAt)) : null;
    if (!date || Number.isNaN(date.getTime())) return "";
    return date.toLocaleString("vi-VN");
}

function detectBank(sms) {
    const text = (sms || "").toLowerCase();
    if (text.includes("vietcombank") || text.includes("vcb")) return "Vietcombank";
    if (text.includes("mbbank") || text.includes("mb:")) return "MB";
    if (text.includes("techcombank") || text.includes("tcb")) return "Techcombank";
    return "";
}

function normalizeBank(value) {
    const text = (value || "").toLowerCase();
    if (text.includes("mb")) return "MB";
    if (text.includes("acb")) return "ACB";
    return value || "";
}

function getField(item, fieldName) {
    if (!item) return null;
    const pascalName = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
    return item[fieldName] ?? item[pascalName] ?? null;
}

function buildOrderPaging(days) {
    const toDate = new Date();
    const fromDate = new Date(toDate);
    fromDate.setDate(fromDate.getDate() - Number(days || 3));

    return {
        platform: PLATFORM.Internal,
        shopName: "",
        shopId: "",
        status: 0,
        provinceCode: null,
        districtCode: null,
        shippingCompany: 0,
        paymentMethod: 0,
        customerType: 0,
        sectionType: 0,
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
        pageNumber: 1,
        pageSize: 50,
        keyword: "",
        orderBy: null,
        orderDirection: null,
        totalRecord: 0,
        totalPages: 0,
        createdBy: "",
    };
}

function getOrderProductCount(order) {
    const products = [
        ...asArray(getField(order, "products")),
        ...asArray(getField(order, "orderProducts")),
        ...asArray(getField(order, "orderCustomizeProducts")),
        ...asArray(getField(order, "orderGiftProducts")),
        ...asArray(getField(order, "orderDetails")),
    ];
    if (Array.isArray(products) && products.length) {
        const quantity = products.reduce((total, product) => total + Number(getField(product, "quantity") || 0), 0);
        return quantity || products.length;
    }

    return Number(getField(order, "quantity") || getField(order, "totalQuantity") || getField(order, "totalProduct") || 0);
}

function asArray(value) {
    return Array.isArray(value) ? value : [];
}

function getOrderAmount(order) {
    const amount = getField(order, "finallyPrice") ?? getField(order, "finalPrice") ?? getField(order, "totalAmount") ?? getField(order, "amount");
    return amount != null ? formatMoney(amount) : "";
}

function SmsCard({ item, onDetail }) {
    const isDebit = item.transactionType === "debit";

    return (
        <article className={`sms-card ${item.classified ? "classified" : ""}`}>
            <div className={`sms-card__stripe ${isDebit ? "debit" : "credit"}`} />
            <header className="sms-card__header">
                <div className="bank-mark">
                    <span className="bank-star">☆</span>
                    <span>{item.bank}</span>
                </div>
                <span className="time">{item.time}</span>
                <div className="tag-list">
                    {item.tags?.length > 0 &&
                        item.tags.map((tag, index) => (
                            <span key={`${tag}-${index}`} className={index === 0 ? "tag orange" : "tag blue"}>
                                {tag}
                            </span>
                        ))}
                    {item.orderId > 0 && <span className="tag orange">{"Mã đơn " + item.orderId}</span>}
                </div>
            </header>
            <p className="sms-content">{item.content}</p>
            <div className="sms-card__footer">
                <InfoBlock label={isDebit ? "Ghi nợ" : "Ghi có"} value={item.amount} danger={isDebit} />
                <InfoBlock label="Số dư" value={item.balance} />
                <InfoBlock label="ND:" value={item.note} neutral />
                <button className="detail-btn" onClick={onDetail}>
                    Chi tiết <i className="fa fa-angle-right" aria-hidden="true" />
                </button>
            </div>
        </article>
    );
}

function InfoBlock({ label, value, danger, neutral }) {
    return (
        <div className="info-block">
            <span>{label}</span>
            <strong className={danger ? "danger" : neutral ? "neutral" : ""}>{value}</strong>
        </div>
    );
}

function SmsDetailModal({ smsId, onClose, onMapped }) {
    const [detail, setDetail] = useState(null);
    const [isLoading, setLoading] = useState(false);
    const [rangeDays, setRangeDays] = useState(3);
    const [orders, setOrders] = useState([]);
    const [isLoadingOrders, setLoadingOrders] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [isSaving, setSaving] = useState(false);

    useEffect(() => {
        if (!smsId) {
            setDetail(null);
            setOrders([]);
            setSelectedOrderId(null);
            setSaving(false);
            return;
        }
        getDetail(smsId);
    }, [smsId]);

    useEffect(() => {
        if (!smsId) return;
        getOrders(rangeDays);
    }, [smsId, rangeDays]);

    async function getDetail(id) {
        setLoading(true);
        const [err, data] = await FinanceServices.GetSmsContentById(id);
        setDetail(!err && data ? mapSmsContent(data) : null);
        setLoading(false);
    }

    async function getOrders(days) {
        setLoadingOrders(true);
        setSelectedOrderId(null);
        const [err, data] = await OrderServices.GetOrdersPage(buildOrderPaging(days));
        if (!err && data) {
            setOrders(data.listObjects || []);
        } else {
            setOrders([]);
        }
        setLoadingOrders(false);
    }

    async function handleSave() {
        if (!selectedOrderId) {
            Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Vui lòng chọn đơn hàng cần map");
            return;
        }

        setSaving(true);
        const [err, data] = await FinanceServices.HandleMapOrder({
            messageId: smsId,
            orderId: selectedOrderId,
        });
        setSaving(false);

        if (!err && data && data.success !== false) {
            Notify(NOTIFY.SUCCESS, NOTIFY.SUCCESS, "Map SMS với đơn hàng thành công");
            onMapped?.();
            onClose();
            return;
        }

        const responseError = err || data;
        const errMsg =
            responseError?.errors && responseError.errors.length > 0
                ? responseError.errors.reduce((prev, item) => `${prev}${item.value}<br/>`, "")
                : responseError?.message || responseError?.data || CONSTANTS.MSG_ERROR;
        Notify(NOTIFY.ERROR, NOTIFY.ERROR, errMsg);
    }

    if (!smsId) return null;

    return (
        <div className="sms-modal-backdrop">
            <div className="sms-modal">
                <div className="sms-modal__header">
                    <h3>Map đơn hàng</h3>
                    <button type="button" className="modal-close" onClick={onClose}>
                        ×
                    </button>
                </div>
                {isLoading && <div className="modal-loading">Đang tải chi tiết SMS...</div>}
                {!isLoading && detail && (
                    <div className="sms-modal__body">
                        <div className="modal-time">{detail.time}</div>
                        <p className="modal-sms-content">{detail.content}</p>
                        <div className="modal-summary">
                            <ModalInfo label="Số tiền" value={detail.amount} danger={detail.transactionType === "debit"} />
                            <ModalInfo label="Số dư" value={detail.balance} />
                            <ModalInfo label="Nội dung" value={detail.note} />
                        </div>
                        <div className="quick-filter">
                            <span>Lọc nhanh</span>
                            <select value={rangeDays} onChange={(e) => setRangeDays(Number(e.target.value))}>
                                <option value="3">3 ngày gần nhất</option>
                                <option value="7">7 ngày gần nhất</option>
                                <option value="30">30 ngày gần nhất</option>
                            </select>
                        </div>
                        <div className="order-list">
                            {isLoadingOrders && <div className="order-match empty-order">Đang tải đơn hàng...</div>}
                            {!isLoadingOrders &&
                                orders.map((order) => {
                                    const orderId = getField(order, "id");
                                    return <OrderMatchItem key={orderId} order={order} checked={selectedOrderId === orderId} onSelect={() => setSelectedOrderId(orderId)} />;
                                })}
                            {!isLoadingOrders && orders.length === 0 && <div className="order-match empty-order">Chưa có dữ liệu đơn hàng phù hợp</div>}
                        </div>
                    </div>
                )}
                {!isLoading && !detail && <div className="modal-loading">Không thể tải chi tiết SMS</div>}
                <div className="sms-modal__footer">
                    <button type="button" className="btn-close-modal" onClick={onClose}>
                        Đóng
                    </button>
                    <button type="button" className="btn-save-modal" onClick={handleSave} disabled={!selectedOrderId || isSaving}>
                        {isSaving ? "Đang lưu..." : "Lưu"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ModalInfo({ label, value, danger }) {
    return (
        <div className="modal-info">
            <span>{label}</span>
            <strong className={danger ? "danger" : ""}>{value}</strong>
        </div>
    );
}

function OrderMatchItem({ order, checked, onSelect }) {
    const orderId = getField(order, "id");
    const orderNumber = getField(order, "orderNumber") || "";
    const customerName = getField(order, "customerName") || getField(order, "receiverName") || "";
    const phoneNumber = getField(order, "phoneNumber") || getField(order, "receiverPhone") || "";
    const productCount = getOrderProductCount(order);
    const totalAmount = getOrderAmount(order);
    const paymentMethodName = getField(order, "paymentMethodName") || getField(order, "paymentMethodText") || "";

    function openOrderDetail(event) {
        event.stopPropagation();
        if (orderId) {
            window.open(`/order-detail/${orderId}`, "_blank");
        }
    }

    return (
        <div className="order-match">
            <label className="order-radio">
                <input type="radio" checked={checked} onChange={onSelect} />
                <span />
            </label>
            <div className="order-main">
                <div className="order-type">Đơn hệ thống</div>
                <div className="order-id-row">
                    <button type="button" onClick={openOrderDetail}>
                        {orderId}
                    </button>
                    {orderNumber && <span>({orderNumber})</span>}
                </div>
                <div>{customerName}</div>
                <div>{phoneNumber}</div>
            </div>
            <div className="order-meta">
                <div>{productCount ? `${productCount} sản phẩm` : ""}</div>
                <div>{paymentMethodName}</div>
            </div>
            <div className="order-total">{totalAmount}</div>
        </div>
    );
}

const Wrapper = styled.div`
    width: 100%;
    background: #f1f1f1;
    min-height: calc(100vh - 70px);
    padding-bottom: 24px;

    .sms-page {
        padding: 0 15px 24px;
    }

    .bank-tabs,
    .status-tabs {
        display: flex;
        align-items: center;
    }

    .bank-tabs {
        margin-top: -8px;
    }

    .tab {
        min-height: 41px;
        border: none;
        border-bottom: 3px solid #dfe2e4;
        background: #f8f9fa;
        color: #666f7b;
        font-size: 14px;
        font-weight: 600;
        padding: 0 20px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        cursor: pointer;
    }

    .tab.active {
        background: #fff;
        border-bottom-color: #64c5b1;
    }

    .count-badge {
        min-width: 16px;
        height: 16px;
        border-radius: 10px;
        padding: 0 4px;
        background: #d70000;
        color: #fff;
        font-size: 11px;
        font-weight: 400;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }

    .filter-bar {
        height: 67px;
        background: #fff;
        display: flex;
        align-items: center;
        gap: 36px;
        padding-left: 21px;
        margin-bottom: 20px;
    }

    .search-box {
        display: flex;
        height: 36px;
    }

    .search-box input {
        width: 295px;
        height: 36px;
        border: 1px solid #d9d9d9;
        border-right: none;
        color: #444;
        font-size: 14px;
        padding: 0 15px;
        outline: none;
    }

    .search-box input::placeholder {
        color: rgba(102, 111, 123, 0.35);
    }

    .search-box button {
        width: 85px;
        height: 36px;
        border: 1px solid #444;
        background: #444;
        color: #fff;
        cursor: pointer;
    }

    .filter-bar select {
        width: 181px;
        height: 36px;
        border: 1px solid #d9d9d9;
        background: #fff;
        color: #444;
        font-size: 14px;
        padding: 0 12px;
        outline: none;
    }

    .sms-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(360px, 1fr));
        gap: 12px 24px;
    }

    .sms-card {
        min-height: 156px;
        position: relative;
        background: #fff;
        border: 1px solid #ededed;
        border-radius: 2px;
        padding: 12px 20px 16px 24px;
    }

    .sms-card.classified {
        background: #fffac9;
    }

    .sms-card__stripe {
        position: absolute;
        left: 0;
        top: 0;
        width: 8px;
        height: 100%;
        border-radius: 2px 0 0 2px;
    }

    .sms-card__stripe.credit {
        background: #28b259;
    }

    .sms-card__stripe.debit {
        background: #d70000;
    }

    .sms-card__header {
        min-height: 24px;
        display: flex;
        align-items: center;
        gap: 15px;
        padding-right: 2px;
    }

    .bank-mark {
        min-width: 60px;
        display: inline-flex;
        align-items: center;
        gap: 1px;
        color: #225fd5;
        font-size: 16px;
        font-weight: 800;
        line-height: 1;
    }

    .bank-star {
        color: #ff1744;
        font-size: 25px;
        font-weight: 400;
        line-height: 14px;
    }

    .time {
        color: #848484;
        font-size: 16px;
        white-space: nowrap;
    }

    .tag-list {
        margin-left: auto;
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        flex-wrap: wrap;
    }

    .tag {
        min-height: 24px;
        border-radius: 20px;
        color: #444;
        font-size: 14px;
        font-weight: 700;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 4px 10px;
        white-space: nowrap;
    }

    .tag.orange {
        background: #febd6a;
    }

    .tag.blue {
        background: #8dd6ff;
    }

    .sms-content {
        color: #000;
        font-size: 16px;
        line-height: 20px;
        margin: 10px 0 16px;
    }

    .sms-card__footer {
        display: grid;
        grid-template-columns: 84px 94px minmax(120px, 1fr) 109px;
        align-items: end;
        gap: 14px;
    }

    .info-block {
        display: flex;
        flex-direction: column;
        gap: 3px;
        line-height: 1;
        min-width: 0;
    }

    .info-block span {
        color: #a0a0a0;
        font-size: 14px;
        font-weight: 400;
    }

    .info-block strong {
        color: #28b259;
        font-size: 16px;
        font-weight: 700;
        min-height: 16px;
        white-space: nowrap;
    }

    .info-block strong.danger {
        color: #d70000;
    }

    .info-block strong.neutral {
        color: #444;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .detail-btn {
        width: 109px;
        height: 32px;
        border: none;
        border-radius: 2px;
        background: #64c5b1;
        color: #fff;
        font-size: 14px;
        font-weight: 700;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        cursor: pointer;
    }

    .empty-state {
        grid-column: 1 / -1;
        background: #fff;
        border: 1px solid #ededed;
        color: #666f7b;
        font-size: 14px;
        padding: 32px;
        text-align: center;
    }

    .sms-footer {
        background: #fff;
        border: 1px solid #ededed;
        margin-top: 16px;
    }

    .sms-modal-backdrop {
        position: fixed;
        inset: 0;
        z-index: 2000;
        background: rgba(0, 0, 0, 0.35);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
    }

    .sms-modal {
        width: 900px;
        max-width: calc(100vw - 40px);
        height: 640px;
        max-height: calc(100vh - 40px);
        background: #fff;
        position: relative;
        display: flex;
        flex-direction: column;
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
    }

    .sms-modal__header {
        height: 52px;
        background: #64c5b1;
        border: 1px solid #d9d9d9;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 23px 0 27px;
        flex-shrink: 0;
    }

    .sms-modal__header h3 {
        color: #fff;
        font-size: 20px;
        font-weight: 600;
        line-height: 20px;
        margin: 0;
    }

    .modal-close {
        width: 30px;
        height: 30px;
        border: none;
        background: transparent;
        color: #fff;
        font-size: 34px;
        font-weight: 200;
        line-height: 24px;
        cursor: pointer;
    }

    .sms-modal__body {
        padding: 15px 24px 90px;
        overflow: hidden;
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
    }

    .modal-loading {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #666f7b;
        font-size: 14px;
    }

    .modal-time {
        color: #848484;
        font-size: 16px;
        margin-bottom: 10px;
    }

    .modal-sms-content {
        width: 552px;
        max-width: 100%;
        color: #000;
        font-size: 14px;
        line-height: 17px;
        margin: 0 0 14px;
        white-space: pre-wrap;
    }

    .modal-summary {
        display: grid;
        grid-template-columns: 160px 190px 1fr;
        gap: 34px;
        margin-bottom: 44px;
    }

    .modal-info {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
    }

    .modal-info span {
        color: #000;
        font-size: 14px;
        white-space: nowrap;
    }

    .modal-info strong {
        color: #28b259;
        font-size: 14px;
        font-weight: 400;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .modal-info strong.danger {
        color: #d70000;
    }

    .quick-filter {
        display: flex;
        align-items: center;
        gap: 28px;
        margin-bottom: 22px;
        flex-shrink: 0;
    }

    .quick-filter span {
        color: #000;
        font-size: 14px;
    }

    .quick-filter select {
        width: 181px;
        height: 36px;
        border: 1px solid #d9d9d9;
        background: #f1f1f1;
        color: #444;
        font-size: 14px;
        padding: 0 12px;
        outline: none;
    }

    .order-list {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        padding-right: 4px;
    }

    .order-match {
        min-height: 125px;
        border: 1px solid #d9d9d9;
        background: #fff;
        color: #666f7b;
        font-size: 14px;
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 10px;
        padding: 16px 24px;
    }

    .empty-order {
        justify-content: center;
    }

    .order-radio {
        width: 34px;
        align-self: stretch;
        border-right: 1px solid #d9d9d9;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: -16px 0 -16px -24px;
        flex-shrink: 0;
        cursor: pointer;
    }

    .order-radio input {
        display: none;
    }

    .order-radio span {
        width: 18px;
        height: 18px;
        border: 1px solid #a0a0a0;
        border-radius: 50%;
        display: block;
        position: relative;
    }

    .order-radio input:checked + span::after {
        content: "";
        position: absolute;
        inset: 4px;
        border-radius: 50%;
        background: #64c5b1;
    }

    .order-main {
        min-width: 210px;
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .order-type {
        width: max-content;
        background: #f96a74;
        color: #fff;
        font-size: 12px;
        padding: 5px 8px;
    }

    .order-id-row {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .order-id-row button {
        border: none;
        background: transparent;
        color: #13aaff;
        font-size: 14px;
        font-weight: 700;
        padding: 0;
        cursor: pointer;
    }

    .order-meta {
        margin-left: auto;
        min-width: 120px;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .order-total {
        min-width: 110px;
        text-align: right;
    }

    .sms-modal__footer {
        position: absolute;
        right: 23px;
        bottom: 20px;
        display: flex;
        gap: 12px;
    }

    .btn-close-modal,
    .btn-save-modal {
        width: 122px;
        height: 34px;
        border: none;
        color: #fff;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
    }

    .btn-close-modal {
        background: #f96a74;
    }

    .btn-save-modal {
        background: #64c5b1;
    }

    .btn-save-modal:disabled {
        background: #a0a0a0;
        cursor: not-allowed;
    }

    @media (max-width: 1200px) {
        .sms-grid {
            grid-template-columns: 1fr;
        }
    }

    @media (max-width: 768px) {
        .filter-bar {
            height: auto;
            padding: 12px;
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
        }

        .search-box input,
        .filter-bar select {
            width: 100%;
        }

        .search-box {
            width: 100%;
        }

        .sms-card__header {
            align-items: flex-start;
            flex-wrap: wrap;
        }

        .tag-list {
            margin-left: 0;
            justify-content: flex-start;
        }

        .sms-card__footer {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .detail-btn {
            width: 100%;
        }
    }
`;
